/* Data type - reflecting the JSON data types. */

export const JsonDataType = {
    String: 'string',
    Number: 'number',
    Date: 'date',
    DateTime: 'datetime',
    Boolean: 'boolean',
} as const;

export type JsonDataType = typeof JsonDataType[keyof typeof JsonDataType];


export class JsonHelper {

    static jsonTypeFromValue(value: any): JsonDataType {
        if (value == null) {
            return JsonDataType.String;
        } else if (Array.isArray(value)) {
            throw new Error('array types not supported');
        }  else {
            switch (typeof (value)) {
                case 'number': return JsonDataType.Number;
                case 'boolean': return JsonDataType.Boolean;
                case 'string': return JsonDataType.String;
                case 'object':
                    if (value instanceof Date) return JsonDataType.DateTime;
                    else throw new Error('object types not supported');
                default: throw new Error('undefined types not supported');
            }
        }
    }

    static convertValueToJson(value: any, jsonType: JsonDataType): any {
        if (value === undefined || value === null) {
            return null;
        } else {
            switch (jsonType) {
                case 'number': return typeof value === 'number' ? value : parseFloat(value);
                case 'boolean': return typeof value === 'boolean' ? value : (value === 'true' || value === '1');
                case 'string': return typeof value === 'string' ? value : value.toString();
                case 'date': return value.toISOString().substring(0,10)
                case 'datetime': return value.toISOString();
                default: return value;
            }
        }
    }

    static convertValueFromJson(value: any, jsonType: JsonDataType): any {
        if (value === undefined || value === null) {
            return value;
        } else {
            switch (jsonType) {
                case 'date': return new Date(value);
                case 'datetime': return new Date(value);
                default: return value;
            }
        }
    }

    static objAttrsAsJsonArray(data: Record<any, any>, attributes: { name: string, jsonType: JsonDataType }[]): any[] {
        return attributes.map(attr => {
            return JsonHelper.convertValueToJson(data[attr.name], attr.jsonType);
        });
    }

    static objAttrsAsJsonObject(values: [], attributes: { name: string, jsonType: JsonDataType }[]): Record<any, any> {
        const obj: Record<any, any> = {};
        for (let i = 0; i < attributes.length; i++) {
            obj[attributes[i].name] = JsonHelper.convertValueFromJson(values[i], attributes[i].jsonType);
        }
        return obj;
    }

    static jsonStringifySorted(obj: any, space = 2): string {
        const sortObject = (v: any): any => {
            if (v === null || typeof v !== 'object' || v instanceof Date) {
                return v;
            }
            if (Array.isArray(v)) {
                return v.map(sortObject);
            }
            return Object.keys(v).sort().reduce((res: any, key) => {
                res[key] = sortObject(v[key]);
                return res;
            }, {});
        };
        return JSON.stringify(sortObject(obj), null, space);
    }

}
